import React, { useState, useEffect, useRef } from 'react'
import { 
  Input, 
  Button, 
  Upload, 
  message, 
  Spin, 
  Avatar, 
  Typography, 
  Dropdown, 
  Modal, 
  Input as AntInput,
  MenuProps,
} from 'antd'
import {
  SendOutlined,
  PaperClipOutlined,
  AudioOutlined,
  PictureOutlined,
  FileTextOutlined,
  UserOutlined,
  RobotOutlined,
  DeleteOutlined,
  HistoryOutlined,
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  PushpinOutlined,
  ShareAltOutlined,
  MoreOutlined,
  CloseOutlined,
  StopOutlined,
} from '@ant-design/icons'
import type { UploadFile, UploadProps } from 'antd'
import './Chat.css'

const { TextArea } = Input
const { Text } = Typography
type ChatTextAreaRef = React.ComponentRef<typeof TextArea>

interface ChatMessage {
  id: string
  type: 'user' | 'assistant'
  content: string
  timestamp: number
  attachments?: Attachment[]
}

interface Attachment {
  id: string
  type: 'image' | 'audio' | 'document'
  name: string
  url?: string
  data?: string
  mimeType: string
}

interface ChatSession {
  id: string
  title: string
  lastMessage: string
  timestamp: number
  messageCount: number
  pinned?: boolean
  group?: string
}

const Chat: React.FC = () => {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [inputValue, setInputValue] = useState('')
  const [loading, setLoading] = useState(false)
  const [fileList, setFileList] = useState<UploadFile[]>([])
  const [sessionId, setSessionId] = useState(() => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`)
  const [sessions, setSessions] = useState<ChatSession[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [sidebarVisible, setSidebarVisible] = useState(true)
  const [renameModalVisible, setRenameModalVisible] = useState(false)
  const [renameSessionId, setRenameSessionId] = useState<string>('')
  const [renameValue, setRenameValue] = useState('')
  const [isRecording, setIsRecording] = useState(false)
  const [recordingTime, setRecordingTime] = useState(0)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const chatContainerRef = useRef<HTMLDivElement>(null)
  const chatTextareaRef = useRef<ChatTextAreaRef>(null)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const audioChunksRef = useRef<Blob[]>([])
  const recordingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // 加载会话列表
  useEffect(() => {
    const sessionList = JSON.parse(localStorage.getItem('chat_sessions') || '[]')
    // 按时间排序：置顶的在前，然后按时间倒序
    const sortedSessions = sessionList.sort((a: ChatSession, b: ChatSession) => {
      if (a.pinned && !b.pinned) return -1
      if (!a.pinned && b.pinned) return 1
      return b.timestamp - a.timestamp
    })
    setSessions(sortedSessions)
  }, [])

  // 加载聊天历史记录
  useEffect(() => {
    const savedMessages = localStorage.getItem(`chat_history_${sessionId}`)
    if (savedMessages) {
      try {
        const parsed = JSON.parse(savedMessages)
        setMessages(parsed)
      } catch (e) {
        console.error('加载聊天历史失败:', e)
      }
    } else {
      setMessages([])
    }
  }, [sessionId])

  // 保存聊天历史记录 - 限制存储大小，避免配额超出
  useEffect(() => {
    if (messages.length > 0) {
      try {
        // 限制存储的消息数量（只保留最近200条消息）
        const messagesToSave = messages.slice(-200)
        const messagesJson = JSON.stringify(messagesToSave)
        
        // 检查数据大小（localStorage 限制通常是 5-10MB）
        const sizeInBytes = new Blob([messagesJson]).size
        const sizeInMB = sizeInBytes / (1024 * 1024)
        
        if (sizeInMB > 4) {
          // 如果数据太大，只保留最近100条消息
          console.warn(`聊天历史数据过大 (${sizeInMB.toFixed(2)}MB)，只保留最近100条消息`)
          const limitedMessages = messages.slice(-100)
          localStorage.setItem(`chat_history_${sessionId}`, JSON.stringify(limitedMessages))
        } else {
          localStorage.setItem(`chat_history_${sessionId}`, messagesJson)
        }
        
        // 同时保存到会话列表
        const sessionList = JSON.parse(localStorage.getItem('chat_sessions') || '[]')
        const sessionIndex = sessionList.findIndex((s: ChatSession) => s.id === sessionId)
        const existingSession = sessionIndex >= 0 ? sessionList[sessionIndex] : null
        const sessionData: ChatSession = {
          id: sessionId,
          title: existingSession?.title || messages[0]?.content?.substring(0, 30) || '新对话',
          lastMessage: messages[messages.length - 1]?.content?.substring(0, 50) || '',
          timestamp: Date.now(),
          messageCount: messages.length,
          pinned: existingSession?.pinned || false,
          group: existingSession?.group,
        }
        if (sessionIndex >= 0) {
          sessionList[sessionIndex] = sessionData
        } else {
          sessionList.unshift(sessionData)
        }
        // 只保留最近100个会话
        const sortedSessions = sessionList
          .slice(0, 100)
          .sort((a: ChatSession, b: ChatSession) => {
            if (a.pinned && !b.pinned) return -1
            if (!a.pinned && b.pinned) return 1
            return b.timestamp - a.timestamp
          })
        localStorage.setItem('chat_sessions', JSON.stringify(sortedSessions))
        setSessions(sortedSessions)
      } catch (e: any) {
        // 处理 Storage 配额超出错误
        if (e.name === 'QuotaExceededError' || e.code === 22) {
          console.error('Storage 配额超出，清理旧数据...')
          // 清理旧的聊天历史（保留最近10个会话）
          try {
            const sessionList = JSON.parse(localStorage.getItem('chat_sessions') || '[]')
            const recentSessions = sessionList.slice(0, 10)
            // 删除其他会话的历史记录
            sessionList.slice(10).forEach((s: ChatSession) => {
              try {
                localStorage.removeItem(`chat_history_${s.id}`)
              } catch (err) {
                console.error(`删除会话 ${s.id} 的历史记录失败:`, err)
              }
            })
            localStorage.setItem('chat_sessions', JSON.stringify(recentSessions))
            setSessions(recentSessions)
            
            // 重新尝试保存当前会话（只保留最近50条消息）
            const limitedMessages = messages.slice(-50)
            localStorage.setItem(`chat_history_${sessionId}`, JSON.stringify(limitedMessages))
            console.log('已清理旧数据，当前会话只保留最近50条消息')
          } catch (cleanupError) {
            console.error('清理 Storage 失败:', cleanupError)
            // 如果清理也失败，至少尝试保存会话列表（不保存消息）
            message.warning('存储空间不足，部分聊天历史可能无法保存')
          }
        } else {
          console.error('保存聊天历史失败:', e)
        }
      }
    }
  }, [messages, sessionId])

  // 自动滚动到底部
  useEffect(() => {
    scrollToBottom()
  }, [messages])

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  // 文件上传处理 - 直接保存文件对象，等待发送到 n8n
  // 文件将在发送消息时作为 multipart/form-data 直接发送到 n8n webhook
  const handleUpload: UploadProps['customRequest'] = async (options) => {
    const { file, onSuccess, onError, onProgress } = options
    const fileObj = file as File & { uid?: string }

    try {
      // 创建文件UID
      const fileUid = (file as UploadFile).uid || Date.now().toString()
      
      const mimeType = fileObj.type || 'application/octet-stream'
      
      console.log('📤 文件已选择:', fileObj.name)
      console.log('📤 文件大小:', (fileObj.size / 1024 / 1024).toFixed(2), 'MB')
      console.log('📤 MIME 类型:', mimeType)
      
      // 创建附件对象（保存文件对象，不转换为 base64）
      const attachment: Attachment = {
        id: Date.now().toString(),
        type: mimeType.startsWith('image/')
          ? 'image'
          : mimeType.startsWith('audio/')
          ? 'audio'
          : 'document',
        name: fileObj.name,
        mimeType: mimeType,
      }
      
      // 为所有文件类型创建 Blob URL，以便点击时可以在页面中打开
      attachment.url = URL.createObjectURL(fileObj)

      // 更新文件列表，状态改为 done，进度100%
      const uploadFile: UploadFile = {
        uid: fileUid,
        name: fileObj.name,
        status: 'done',
        percent: 100,
        originFileObj: fileObj as any,
      }
      // 将attachment保存到uploadFile中，以便后续使用
      ;(uploadFile as any).attachment = attachment
      // 保存原始文件对象，用于发送到 n8n
      ;(uploadFile as any).fileObj = fileObj
      
      setFileList((prev) => {
        const existing = prev.find(f => f.uid === fileUid)
        if (existing) {
          return prev.map(f => f.uid === fileUid ? uploadFile : f)
        }
        return [...prev, uploadFile]
      })
      
      // 调用 onProgress 和 onSuccess 回调
      onProgress?.({ percent: 100 })
      onSuccess?.(attachment, fileObj as any)
      
      console.log('✅ 文件已准备就绪:', fileObj.name, '等待发送到 n8n')
    } catch (error) {
      const fileUid = (file as UploadFile).uid || Date.now().toString()
      
      // 更新文件列表，状态改为 error
      setFileList((prev) => 
        prev.map(f => 
          f.uid === fileUid 
            ? { ...f, status: 'error' as const }
            : f
        )
      )
      
      onError?.(error as Error)
    }
  }

  // 移除文件
  const handleRemove = (file: UploadFile) => {
    setFileList((prev) => prev.filter((item) => item.uid !== file.uid))
  }

  // 发送消息
  const handleSend = async () => {
    if (!inputValue.trim() && fileList.length === 0) {
      message.warning('请输入消息或上传文件')
      return
    }

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      type: 'user',
      content: inputValue.trim() || '已上传文件',
      timestamp: Date.now(),
      attachments: fileList.map((file) => {
        const attachment = (file as any).attachment as Attachment
        return attachment || {
          id: file.uid || Date.now().toString(),
          type: 'document',
          name: file.name || '文件',
          mimeType: file.type || 'application/octet-stream',
        }
      }),
    }

      setMessages((prev) => {
        const newMessages = [...prev, userMessage]
        console.log('用户消息已添加，总消息数:', newMessages.length)
        return newMessages
      })
      setInputValue('')
      
      setLoading(true)
      console.log('开始发送消息到n8n工作流...')

    try {
      // 调用n8n工作流
      // Production URL: http://localhost:5678/webhook/doctor-chat
      // 使用代理路径，避免CORS问题
      const n8nWebhookUrl = import.meta.env.VITE_N8N_WEBHOOK_URL || '/webhook/doctor-chat'
      console.log('发送请求到n8n:', n8nWebhookUrl)
      
      // 准备 FormData，将文件直接作为 multipart/form-data 发送
      const formData = new FormData()
      formData.append('message', userMessage.content)
      formData.append('chatInput', userMessage.content)
      formData.append('text', userMessage.content)
      formData.append('sessionId', sessionId)
      
      // 将文件添加到 FormData
      fileList.forEach((file, index) => {
        const fileObj = (file as any).fileObj as File
        if (fileObj) {
          // 使用 files[] 作为字段名，n8n 可以接收多个文件
          formData.append(`files`, fileObj)
          // 同时添加文件元数据
          const attachment = (file as any).attachment as Attachment
          if (attachment) {
            formData.append(`attachments[${index}][name]`, attachment.name || fileObj.name)
            formData.append(`attachments[${index}][mimeType]`, attachment.mimeType || fileObj.type || 'application/octet-stream')
            formData.append(`attachments[${index}][type]`, attachment.type || 'document')
          }
        }
      })
      
      console.log('请求数据:', {
        message: userMessage.content,
        chatInput: userMessage.content,
        text: userMessage.content,
        sessionId: sessionId,
        filesCount: fileList.length,
      })

      // 确保使用POST方法调用webhook
      console.log('=== 发送请求到 n8n (multipart/form-data) ===')
      console.log('URL:', n8nWebhookUrl)
      console.log('完整请求URL:', window.location.origin + n8nWebhookUrl)
      console.log('文件数量:', fileList.length)
      
      let response
      try {
        response = await fetch(n8nWebhookUrl, {
          method: 'POST',
          // 不设置 Content-Type，让浏览器自动设置 multipart/form-data 边界
          body: formData,
          // 确保不会转换为GET请求
          cache: 'no-cache',
          redirect: 'follow',
        })
      } catch (fetchError) {
        console.error('❌ Fetch 请求失败:', fetchError)
        console.error('错误类型:', fetchError instanceof Error ? fetchError.name : typeof fetchError)
        console.error('错误消息:', fetchError instanceof Error ? fetchError.message : String(fetchError))
        
        // 检查是否是网络错误
        if (fetchError instanceof TypeError && fetchError.message.includes('Failed to fetch')) {
          throw new Error('无法连接到n8n服务。请检查：\n1. n8n服务是否运行在 http://localhost:5678\n2. 前端开发服务器是否正常运行\n3. Vite代理配置是否正确')
        }
        throw fetchError
      }

      console.log('响应状态:', response.status, response.statusText)
      console.log('响应头:', Object.fromEntries(response.headers.entries()))
      
      // 检查响应状态
      if (response.status === 404) {
        const errorText = await response.text()
        console.error('❌ 404错误 - Webhook未找到')
        console.error('响应内容:', errorText)
        throw new Error(`Webhook未找到 (404)。请检查：\n1. n8n工作流是否已激活\n2. Webhook路径是否正确: /webhook/doctor-chat\n3. 工作流ID是否正确: doctor-chat1213`)
      }

      if (!response.ok) {
        const errorText = await response.text()
        console.error('HTTP错误响应:', errorText)
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`)
      }

      // 检查响应是否有内容
      // 注意：响应流只能读取一次，所以需要根据Content-Type选择合适的读取方式
      const contentType = response.headers.get('content-type') || ''
      const contentLength = response.headers.get('content-length')
      
      console.log('=== n8n响应详情 ===')
      console.log('响应状态码:', response.status)
      console.log('响应头:', Object.fromEntries(response.headers.entries()))
      console.log('Content-Type:', contentType)
      console.log('Content-Length:', contentLength)
      
      let data: any = null
      let responseText = ''
      
      // 如果Content-Length为0，响应体为空
      if (contentLength === '0') {
        console.warn('⚠️ Content-Length为0，响应体为空')
        const errorMessage: ChatMessage = {
          id: (Date.now() + 1).toString(),
          type: 'assistant',
          content: '抱歉，工作流返回了空响应。请检查：\n1. n8n工作流是否正常运行\n2. Ollama服务是否运行\n3. 工作流执行日志中的错误信息\n\n请稍后重试或联系管理员。',
          timestamp: Date.now(),
        }
        setMessages((prev) => [...prev, errorMessage])
        message.error('工作流返回空响应，请检查n8n工作流和Ollama服务')
        setLoading(false)
        return
      }
      
      try {
        // 先克隆响应，以便可以同时读取文本和JSON
        const clonedResponse = response.clone()
        
        // 根据Content-Type选择读取方式
        if (contentType.includes('application/json')) {
          // 如果是JSON，尝试直接解析
          try {
            data = await response.json()
            console.log('✅ 成功解析JSON响应')
            console.log('响应数据:', data)
            responseText = JSON.stringify(data)
          } catch (jsonError: any) {
            console.warn('JSON解析失败，尝试读取文本:', jsonError.message)
            // 如果JSON解析失败，使用克隆的响应读取文本
            try {
              responseText = await clonedResponse.text()
              console.log('响应文本长度:', responseText.length)
              console.log('响应文本内容（前500字符）:', responseText.substring(0, 500))
              console.log('响应文本内容（完整）:', responseText)
              
              if (responseText && responseText.trim().length > 0) {
                try {
                  data = JSON.parse(responseText)
                  console.log('✅ 从文本手动解析JSON成功')
                } catch (parseError) {
                  console.warn('手动JSON解析也失败，作为纯文本处理:', parseError)
                  data = { text: responseText }
                }
              }
            } catch (textError: any) {
              console.error('❌ 读取响应文本也失败:', textError)
            }
          }
        } else {
          // 如果不是JSON，读取文本
          responseText = await response.text()
          console.log('响应文本长度:', responseText.length)
          console.log('响应文本内容（前500字符）:', responseText.substring(0, 500))
          console.log('响应文本内容（完整）:', responseText)
          
          if (responseText && responseText.trim().length > 0) {
            try {
              data = JSON.parse(responseText)
            } catch (e) {
              // 如果不是JSON，作为纯文本处理
              data = { text: responseText }
            }
          }
        }
      } catch (readError: any) {
        console.error('❌ 读取响应失败:', readError)
        console.error('错误详情:', readError.message)
        console.error('错误堆栈:', readError.stack)
      }

      // 检查响应是否为空
      if ((!responseText || responseText.trim().length === 0) && (!data || (typeof data === 'object' && Object.keys(data).length === 0))) {
        console.error('❌ 响应为空！')
        console.error('可能的原因：')
        console.error('1. n8n工作流Webhook Response节点没有返回数据')
        console.error('2. 工作流执行超时或中断')
        console.error('3. Output Formatter节点输出为空')
        console.error('4. Webhook Response节点配置错误')
        console.error('5. AI Agent节点执行失败或超时')
        console.error('6. Ollama服务未运行或模型不可用')
        
        // 即使响应为空，也返回一个错误消息给用户
        const errorMessage: ChatMessage = {
          id: (Date.now() + 1).toString(),
          type: 'assistant',
          content: '抱歉，工作流执行失败或超时。请检查：\n1. n8n工作流是否正常运行\n2. Ollama服务是否运行\n3. 工作流执行日志中的错误信息\n\n请稍后重试或联系管理员。',
          timestamp: Date.now(),
        }
        setMessages((prev) => [...prev, errorMessage])
        message.error('工作流执行失败，请检查n8n工作流和Ollama服务')
        setLoading(false)
        return
      }
      
      if (data) {
        console.log('响应数据类型:', Array.isArray(data) ? '数组' : typeof data)
        console.log('响应数据键:', Array.isArray(data) ? `数组长度: ${data.length}` : Object.keys(data))
      }
      
      // 如果响应是数组，取第一个元素
      if (Array.isArray(data) && data.length > 0) {
        console.log('响应是数组，使用第一个元素')
        data = data[0]
      }
      
      // 解析n8n工作流响应
      // 根据Output Formatter的输出格式：{ output: "文本", text: "文本", content: "文本", responseType: "text" }
      let assistantContent = ''
      
      // 优先级1: 直接字符串字段（最常用）
      if (typeof data.output === 'string' && data.output.trim().length > 0) {
        assistantContent = data.output
      } else if (typeof data.text === 'string' && data.text.trim().length > 0) {
        assistantContent = data.text
      } else if (typeof data.content === 'string' && data.content.trim().length > 0) {
        assistantContent = data.content
      }
      // 优先级2: 对象中的字符串字段
      else if (data.output && typeof data.output === 'object') {
        const output = data.output
        if (typeof output.content === 'string') {
          assistantContent = output.content
        } else if (typeof output.text === 'string') {
          assistantContent = output.text
        } else if (typeof output.output === 'string') {
          assistantContent = output.output
        } else {
          // 尝试从对象中提取任何字符串字段
          const textFields = ['text', 'content', 'message', 'answer', 'response', 'output']
          for (const field of textFields) {
            if (output[field] && typeof output[field] === 'string') {
              assistantContent = output[field]
              break
            }
          }
        }
      }
      // 优先级3: 其他可能的字段
      else if (data.message && typeof data.message === 'string') {
        assistantContent = data.message
      } else if (data.answer && typeof data.answer === 'string') {
        assistantContent = data.answer
      } else if (data.response && typeof data.response === 'string') {
        assistantContent = data.response
      }
      // 优先级4: 最后尝试：查找任何包含文本的字段
      else {
        const textFields = ['output', 'text', 'content', 'message', 'answer', 'response']
        for (const field of textFields) {
          if (data[field]) {
            if (typeof data[field] === 'string') {
              assistantContent = data[field]
              break
            } else if (typeof data[field] === 'object') {
              // 如果是对象，尝试提取其中的文本字段
              const obj = data[field]
              if (typeof obj.content === 'string') {
                assistantContent = obj.content
                break
              } else if (typeof obj.text === 'string') {
                assistantContent = obj.text
                break
              }
            }
          }
        }
      }
      
      // 确保有内容
      if (!assistantContent || assistantContent.trim().length === 0) {
        console.error('❌ 无法从响应中提取内容')
        console.error('响应数据:', JSON.stringify(data, null, 2))
        assistantContent = '抱歉，AI没有返回有效回复。响应数据格式可能不正确。'
      } else {
        console.log('✅ 成功提取AI回复，长度:', assistantContent.length)
        console.log('回复预览:', assistantContent.substring(0, 200))
      }

      const assistantMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: assistantContent,
        timestamp: Date.now(),
      }

      console.log('准备添加助手消息到聊天列表')
      setMessages((prev) => {
        const newMessages = [...prev, assistantMessage]
        console.log('消息列表更新，总消息数:', newMessages.length)
        return newMessages
      })
      
      // 文件已成功发送到服务器，清空文件列表
      setFileList([])
      
      setLoading(false)
      console.log('✅ 消息发送完成')
    } catch (error) {
      console.error('❌ 发送消息失败:', error)
      const errorMsg = error instanceof Error ? error.message : '未知错误'
      console.error('错误类型:', error instanceof Error ? error.constructor.name : typeof error)
      console.error('错误详情:', errorMsg)
      console.error('完整错误对象:', error)
      
      // 确保loading状态被重置
      setLoading(false)
      
      // 文件上传失败，更新文件状态为 error
      setFileList((prev) => 
        prev.map(f => ({
          ...f,
          status: 'error' as const,
        }))
      )
      
      // 显示更详细的错误信息
      let errorContent = '抱歉，服务暂时不可用，请稍后重试。'
      if (errorMsg.includes('空响应')) {
        errorContent = 'n8n工作流返回空响应。请检查：\n1. n8n工作流是否已激活\n2. 工作流执行是否成功\n3. Webhook Response节点是否正确连接'
      } else if (errorMsg.includes('Failed to fetch') || errorMsg.includes('NetworkError') || errorMsg.includes('无法连接到n8n')) {
        errorContent = '无法连接到n8n服务。请检查：\n1. n8n服务是否运行在 http://localhost:5678\n2. 前端开发服务器是否正常运行\n3. Vite代理配置是否正确\n4. 浏览器控制台是否有CORS错误'
      } else if (errorMsg.includes('Webhook未找到') || errorMsg.includes('404')) {
        errorContent = 'Webhook未找到。请检查：\n1. 在n8n界面中激活工作流 "doctor-chat1213"\n2. 确认Webhook路径为: /webhook/doctor-chat\n3. 检查n8n服务是否正常运行'
      } else if (errorMsg.includes('HTTP error')) {
        errorContent = `服务器错误: ${errorMsg}`
      }
      
      message.error('发送消息失败: ' + errorMsg)
      
      const errorMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: errorContent,
        timestamp: Date.now(),
      }
      setMessages((prev) => [...prev, errorMessage])
    } finally {
      setLoading(false)
    }
  }

  // 键盘快捷键
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  // 开始语音录制
  const handleStartRecording = async () => {
    try {
      // 检查浏览器支持
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        message.error('您的浏览器不支持语音录制功能，请使用 Chrome、Edge 或 Firefox 浏览器')
        return
      }
      
      // 检查是否在安全上下文中（HTTPS 或 localhost）
      if (location.protocol !== 'https:' && location.hostname !== 'localhost' && location.hostname !== '127.0.0.1') {
        message.warning('语音录制需要在 HTTPS 环境下使用，当前环境可能无法访问麦克风')
      }
      
      // 请求麦克风权限
      let stream: MediaStream
      try {
        stream = await navigator.mediaDevices.getUserMedia({ 
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          } 
        })
      } catch (permissionError: any) {
        console.error('麦克风权限错误:', permissionError)
        
        // 根据错误类型提供不同的提示
        if (permissionError.name === 'NotAllowedError' || permissionError.name === 'PermissionDeniedError') {
          message.error('麦克风权限被拒绝，请在浏览器设置中允许访问麦克风')
        } else if (permissionError.name === 'NotFoundError' || permissionError.name === 'DevicesNotFoundError') {
          message.error('未检测到麦克风设备，请检查设备连接')
        } else if (permissionError.name === 'NotReadableError' || permissionError.name === 'TrackStartError') {
          message.error('麦克风被其他应用占用，请关闭其他使用麦克风的程序后重试')
        } else if (permissionError.name === 'OverconstrainedError') {
          message.error('麦克风不支持所需的音频格式，请尝试使用其他浏览器')
        } else {
          message.error(`无法访问麦克风: ${permissionError.message || '未知错误'}`)
        }
        return
      }
      
      // 检查 MediaRecorder 支持
      if (!window.MediaRecorder) {
        message.error('您的浏览器不支持 MediaRecorder API')
        stream.getTracks().forEach(track => track.stop())
        return
      }
      
      // 确定支持的 MIME 类型
      let mimeType = 'audio/webm;codecs=opus'
      const supportedTypes = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/ogg;codecs=opus',
        'audio/mp4',
        'audio/wav',
      ]
      
      // 检查浏览器支持哪种格式
      for (const type of supportedTypes) {
        if (MediaRecorder.isTypeSupported(type)) {
          mimeType = type
          break
        }
      }
      
      console.log('使用音频格式:', mimeType)
      
      // 创建 MediaRecorder
      let mediaRecorder: MediaRecorder
      try {
        mediaRecorder = new MediaRecorder(stream, {
          mimeType: mimeType
        })
      } catch (recorderError: any) {
        console.error('创建 MediaRecorder 失败:', recorderError)
        message.error(`无法创建录音器: ${recorderError.message || '不支持的音频格式'}`)
        stream.getTracks().forEach(track => track.stop())
        return
      }
      
      mediaRecorderRef.current = mediaRecorder
      audioChunksRef.current = []
      
      // 监听错误事件
      mediaRecorder.onerror = (event: any) => {
        console.error('MediaRecorder 错误:', event)
        message.error('录音过程中发生错误，请重试')
        setIsRecording(false)
        if (recordingTimerRef.current) {
          clearInterval(recordingTimerRef.current)
          recordingTimerRef.current = null
        }
        stream.getTracks().forEach(track => track.stop())
      }
      
      // 监听数据可用事件
      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          audioChunksRef.current.push(event.data)
          console.log('收到音频数据块，大小:', event.data.size, 'bytes')
        }
      }
      
      // 监听录制停止事件
      mediaRecorder.onstop = () => {
        console.log('录制停止，数据块数量:', audioChunksRef.current.length)
        
        if (audioChunksRef.current.length === 0) {
          message.warning('录制失败，未获取到音频数据')
          stream.getTracks().forEach(track => track.stop())
          return
        }
        
        // 根据 MIME 类型确定文件扩展名
        const extension = mimeType.includes('webm') ? 'webm' 
          : mimeType.includes('ogg') ? 'ogg'
          : mimeType.includes('mp4') ? 'm4a'
          : mimeType.includes('wav') ? 'wav'
          : 'webm'
        
        // 创建音频 Blob
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType })
        
        // 创建 File 对象
        const audioFile = new File([audioBlob], `voice_${Date.now()}.${extension}`, {
          type: mimeType,
          lastModified: Date.now(),
        })
        
        // 创建附件对象
        const attachment: Attachment = {
          id: Date.now().toString(),
          type: 'audio',
          name: audioFile.name,
          mimeType: mimeType,
          url: URL.createObjectURL(audioBlob),
        }
        
        // 创建 UploadFile 对象
        const fileUid = Date.now().toString()
        const uploadFile: UploadFile = {
          uid: fileUid,
          name: audioFile.name,
          status: 'done',
          percent: 100,
          originFileObj: audioFile as any,
        }
        
        // 保存附件和文件对象
        ;(uploadFile as any).attachment = attachment
        ;(uploadFile as any).fileObj = audioFile
        ;(uploadFile as any).fileUrl = attachment.url
        
        // 添加到文件列表
        setFileList((prev) => [...prev, uploadFile])
        
        // 停止所有音频轨道
        stream.getTracks().forEach(track => track.stop())
        
        message.success('语音录制完成')
      }
      
      // 开始录制（每 1000ms 收集一次数据）
      try {
        mediaRecorder.start(1000) // 每 1 秒触发一次 dataavailable 事件
        setIsRecording(true)
        setRecordingTime(0)
        
        // 启动计时器
        recordingTimerRef.current = setInterval(() => {
          setRecordingTime((prev) => prev + 1)
        }, 1000)
        
        console.log('开始录制语音，格式:', mimeType)
        message.success('开始录制语音...')
      } catch (startError: any) {
        console.error('启动录制失败:', startError)
        message.error(`启动录制失败: ${startError.message || '未知错误'}`)
        stream.getTracks().forEach(track => track.stop())
      }
    } catch (error: any) {
      console.error('录制过程发生错误:', error)
      message.error(`录制失败: ${error.message || '未知错误，请检查浏览器控制台'}`)
    }
  }
  
  // 停止语音录制
  const handleStopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop()
      setIsRecording(false)
      
      // 清除计时器
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current)
        recordingTimerRef.current = null
      }
      
      setRecordingTime(0)
      console.log('停止录制语音')
    }
  }
  
  // 格式化录制时间
  const formatRecordingTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }
  
  // 清理资源
  useEffect(() => {
    return () => {
      // 组件卸载时清理
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current)
      }
      if (mediaRecorderRef.current && isRecording) {
        mediaRecorderRef.current.stop()
      }
    }
  }, [isRecording])

  // 创建新对话
  const handleNewChat = () => {
    const newSessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    setSessionId(newSessionId)
    setMessages([])
    setInputValue('')
    setFileList([])
  }

  // 切换会话
  const handleSwitchSession = (id: string) => {
    setSessionId(id)
    setInputValue('')
    setFileList([])
  }

  // 获取分组后的会话列表
  const getGroupedSessions = () => {
    const filtered = searchQuery
      ? sessions.filter(s => 
          s.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          s.lastMessage.toLowerCase().includes(searchQuery.toLowerCase())
        )
      : sessions

    const now = Date.now()
    const today = new Date().setHours(0, 0, 0, 0)
    const sevenDaysAgo = now - 7 * 24 * 60 * 60 * 1000
    const thirtyDaysAgo = now - 30 * 24 * 60 * 60 * 1000

    const pinned = filtered.filter(s => s.pinned)
    const todaySessions = filtered.filter(s => !s.pinned && s.timestamp >= today)
    const weekSessions = filtered.filter(s => !s.pinned && s.timestamp >= sevenDaysAgo && s.timestamp < today)
    const monthSessions = filtered.filter(s => !s.pinned && s.timestamp >= thirtyDaysAgo && s.timestamp < sevenDaysAgo)
    const olderSessions = filtered.filter(s => !s.pinned && s.timestamp < thirtyDaysAgo)

    // 按月分组旧会话
    const monthlyGroups: { [key: string]: ChatSession[] } = {}
    olderSessions.forEach(session => {
      const date = new Date(session.timestamp)
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
      if (!monthlyGroups[monthKey]) {
        monthlyGroups[monthKey] = []
      }
      monthlyGroups[monthKey].push(session)
    })

    return { pinned, todaySessions, weekSessions, monthSessions, monthlyGroups }
  }

  // 重命名会话
  const handleRename = (id: string) => {
    const session = sessions.find(s => s.id === id)
    setRenameSessionId(id)
    setRenameValue(session?.title || '')
    setRenameModalVisible(true)
  }

  const handleRenameConfirm = () => {
    if (!renameValue.trim()) {
      message.warning('请输入会话名称')
      return
    }
    const sessionList = JSON.parse(localStorage.getItem('chat_sessions') || '[]')
    const sessionIndex = sessionList.findIndex((s: ChatSession) => s.id === renameSessionId)
    if (sessionIndex >= 0) {
      sessionList[sessionIndex].title = renameValue.trim()
      localStorage.setItem('chat_sessions', JSON.stringify(sessionList))
      setSessions(sessionList)
      message.success('重命名成功')
    }
    setRenameModalVisible(false)
    setRenameSessionId('')
    setRenameValue('')
  }

  // 置顶/取消置顶
  const handleTogglePin = (id: string) => {
    const sessionList = JSON.parse(localStorage.getItem('chat_sessions') || '[]')
    const sessionIndex = sessionList.findIndex((s: ChatSession) => s.id === id)
    if (sessionIndex >= 0) {
      sessionList[sessionIndex].pinned = !sessionList[sessionIndex].pinned
      const sortedSessions = sessionList.sort((a: ChatSession, b: ChatSession) => {
        if (a.pinned && !b.pinned) return -1
        if (!a.pinned && b.pinned) return 1
        return b.timestamp - a.timestamp
      })
      localStorage.setItem('chat_sessions', JSON.stringify(sortedSessions))
      setSessions(sortedSessions)
      message.success(sessionList[sessionIndex].pinned ? '已置顶' : '已取消置顶')
    }
  }

  // 分享会话
  const handleShare = (id: string) => {
    const session = sessions.find(s => s.id === id)
    if (session) {
      const shareData = {
        title: session.title,
        messages: JSON.parse(localStorage.getItem(`chat_history_${id}`) || '[]'),
      }
      const shareText = JSON.stringify(shareData, null, 2)
      
      if (navigator.share) {
        navigator.share({
          title: session.title,
          text: shareText,
        }).catch(() => {
          // 如果分享失败，复制到剪贴板
          navigator.clipboard.writeText(shareText)
          message.success('会话内容已复制到剪贴板')
        })
      } else {
        navigator.clipboard.writeText(shareText)
        message.success('会话内容已复制到剪贴板')
      }
    }
  }

  // 删除会话
  const handleDelete = (id: string) => {
    Modal.confirm({
      title: '确认删除',
      content: '确定要删除这个对话吗？此操作不可恢复。',
      onOk: () => {
        const sessionList = JSON.parse(localStorage.getItem('chat_sessions') || '[]')
        const filtered = sessionList.filter((s: ChatSession) => s.id !== id)
        localStorage.setItem('chat_sessions', JSON.stringify(filtered))
        localStorage.removeItem(`chat_history_${id}`)
        setSessions(filtered)
        if (id === sessionId) {
          handleNewChat()
        }
        message.success('删除成功')
      },
    })
  }

  // 获取会话菜单
  const getSessionMenu = (session: ChatSession): MenuProps => ({
    items: [
      {
        key: 'rename',
        label: '重命名',
        icon: <EditOutlined />,
        onClick: () => handleRename(session.id),
      },
      {
        key: 'pin',
        label: session.pinned ? '取消置顶' : '置顶',
        icon: <PushpinOutlined />,
        onClick: () => handleTogglePin(session.id),
      },
      {
        key: 'share',
        label: '分享',
        icon: <ShareAltOutlined />,
        onClick: () => handleShare(session.id),
      },
      {
        type: 'divider',
      },
      {
        key: 'delete',
        label: '删除',
        icon: <DeleteOutlined />,
        danger: true,
        onClick: () => handleDelete(session.id),
      },
    ],
  })

  const { pinned, todaySessions, weekSessions, monthSessions, monthlyGroups } = getGroupedSessions()

  return (
    <div className="chat-layout">
      {/* 侧边栏 */}
      {sidebarVisible && (
        <div className="chat-sidebar">
          <div className="chat-sidebar-header">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              block
              onClick={handleNewChat}
              className="new-chat-button"
            >
              开启新对话
            </Button>
            <div className="chat-search">
              <Input
                prefix={<SearchOutlined />}
                placeholder="搜索对话..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                allowClear
              />
            </div>
          </div>
          <div className="chat-sidebar-content">
            {/* 置顶会话 */}
            {pinned.length > 0 && (
              <div className="chat-session-group">
                <div className="chat-session-group-title">置顶</div>
                {pinned.map((session) => (
                  <div
                    key={session.id}
                    className={`chat-session-item ${session.id === sessionId ? 'active' : ''}`}
                    onClick={() => handleSwitchSession(session.id)}
                  >
                    <div className="chat-session-content">
                      <div className="chat-session-title">
                        {session.pinned && <PushpinOutlined className="pin-icon" />}
                        {session.title}
                      </div>
                      <div className="chat-session-preview">{session.lastMessage || '暂无消息'}</div>
                    </div>
                    <Dropdown menu={getSessionMenu(session)} trigger={['click']}>
                      <Button
                        type="text"
                        icon={<MoreOutlined />}
                        className="chat-session-menu"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </Dropdown>
                  </div>
                ))}
              </div>
            )}

            {/* 今天 */}
            {todaySessions.length > 0 && (
              <div className="chat-session-group">
                <div className="chat-session-group-title">今天</div>
                {todaySessions.map((session) => (
                  <div
                    key={session.id}
                    className={`chat-session-item ${session.id === sessionId ? 'active' : ''}`}
                    onClick={() => handleSwitchSession(session.id)}
                  >
                    <div className="chat-session-content">
                      <div className="chat-session-title">{session.title}</div>
                      <div className="chat-session-preview">{session.lastMessage || '暂无消息'}</div>
                    </div>
                    <Dropdown menu={getSessionMenu(session)} trigger={['click']}>
                      <Button
                        type="text"
                        icon={<MoreOutlined />}
                        className="chat-session-menu"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </Dropdown>
                  </div>
                ))}
              </div>
            )}

            {/* 7天内 */}
            {weekSessions.length > 0 && (
              <div className="chat-session-group">
                <div className="chat-session-group-title">7天内</div>
                {weekSessions.map((session) => (
                  <div
                    key={session.id}
                    className={`chat-session-item ${session.id === sessionId ? 'active' : ''}`}
                    onClick={() => handleSwitchSession(session.id)}
                  >
                    <div className="chat-session-content">
                      <div className="chat-session-title">{session.title}</div>
                      <div className="chat-session-preview">{session.lastMessage || '暂无消息'}</div>
                    </div>
                    <Dropdown menu={getSessionMenu(session)} trigger={['click']}>
                      <Button
                        type="text"
                        icon={<MoreOutlined />}
                        className="chat-session-menu"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </Dropdown>
                  </div>
                ))}
              </div>
            )}

            {/* 30天内 */}
            {monthSessions.length > 0 && (
              <div className="chat-session-group">
                <div className="chat-session-group-title">30天内</div>
                {monthSessions.map((session) => (
                  <div
                    key={session.id}
                    className={`chat-session-item ${session.id === sessionId ? 'active' : ''}`}
                    onClick={() => handleSwitchSession(session.id)}
                  >
                    <div className="chat-session-content">
                      <div className="chat-session-title">{session.title}</div>
                      <div className="chat-session-preview">{session.lastMessage || '暂无消息'}</div>
                    </div>
                    <Dropdown menu={getSessionMenu(session)} trigger={['click']}>
                      <Button
                        type="text"
                        icon={<MoreOutlined />}
                        className="chat-session-menu"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </Dropdown>
                  </div>
                ))}
              </div>
            )}

            {/* 按月分组 */}
            {Object.keys(monthlyGroups).map((monthKey) => {
              const [year, month] = monthKey.split('-')
              return (
                <div key={monthKey} className="chat-session-group">
                  <div className="chat-session-group-title">{`${year}年${parseInt(month)}月`}</div>
                  {monthlyGroups[monthKey].map((session) => (
                    <div
                      key={session.id}
                      className={`chat-session-item ${session.id === sessionId ? 'active' : ''}`}
                      onClick={() => handleSwitchSession(session.id)}
                    >
                      <div className="chat-session-content">
                        <div className="chat-session-title">{session.title}</div>
                        <div className="chat-session-preview">{session.lastMessage || '暂无消息'}</div>
                      </div>
                      <Dropdown menu={getSessionMenu(session)} trigger={['click']}>
                        <Button
                          type="text"
                          icon={<MoreOutlined />}
                          className="chat-session-menu"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </Dropdown>
                    </div>
                  ))}
                </div>
              )
            })}

            {pinned.length === 0 && todaySessions.length === 0 && weekSessions.length === 0 && 
             monthSessions.length === 0 && Object.keys(monthlyGroups).length === 0 && (
              <div className="chat-empty-sidebar">
                <Text type="secondary">暂无对话记录</Text>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 主内容区 */}
      <div className="chat-content">
        <div className="chat-container" ref={chatContainerRef}>
          {/* 顶部工具栏 */}
          <div className="chat-header">
            <Button
              type="text"
              icon={sidebarVisible ? <CloseOutlined /> : <HistoryOutlined />}
              onClick={() => setSidebarVisible(!sidebarVisible)}
              className="sidebar-toggle"
            />
            <Text className="chat-header-title">
              {sessions.find(s => s.id === sessionId)?.title || '新对话'}
            </Text>
          </div>
          {/* 消息列表 */}
          <div className="chat-messages">
            {messages.length === 0 ? (
              <div className="chat-empty">
                <div className="chat-empty-icon">
                  <RobotOutlined style={{ fontSize: 64, color: '#4A90E2' }} />
                </div>
                <Text className="chat-empty-title">Hi, 欢迎使用岐黄智医</Text>
                <Text className="chat-empty-subtitle">我是您的中医AI助手，可以为您提供医疗咨询和建议</Text>
                <div className="chat-suggestions">
                  <div className="suggestion-card">
                    <FileTextOutlined />
                    <div>
                      <Text strong>活力指南</Text>
                      <Text type="secondary">有氧和无氧运动科普</Text>
                    </div>
                  </div>
                  <div className="suggestion-card">
                    <FileTextOutlined />
                    <div>
                      <Text strong>养生不麻烦</Text>
                      <Text type="secondary">久坐如何预防腰背疼痛</Text>
                    </div>
                  </div>
                  <div className="suggestion-card">
                    <PictureOutlined />
                    <div>
                      <Text strong>识图求知</Text>
                      <Text type="secondary">帮我看看今天做什么菜</Text>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {messages.map((msg) => (
                  <div key={msg.id} className={`chat-message ${msg.type}`}>
                    <Avatar
                      className="chat-avatar"
                      icon={msg.type === 'user' ? <UserOutlined /> : <RobotOutlined />}
                      style={{
                        backgroundColor: msg.type === 'user' ? '#4A90E2' : '#52C41A',
                      }}
                    />
                    <div className="chat-message-content">
                      <div className="chat-message-bubble">
                        {msg.attachments && msg.attachments.length > 0 && (
                          <div className="chat-attachments">
                            {msg.attachments.map((att) => {
                              // 处理文件点击打开
                              const handleFileClick = () => {
                                if (att.url) {
                                  // 使用文件 URL 打开
                                  window.open(att.url, '_blank')
                                } else {
                                  message.warning('文件 URL 不可用')
                                }
                              }
                              
                              return (
                                <div 
                                  key={att.id} 
                                  className="chat-attachment"
                                  onClick={handleFileClick}
                                  style={{ cursor: 'pointer' }}
                                  title={att.type === 'image' ? '点击查看大图' : '点击打开文件'}
                                >
                                  {att.type === 'image' && att.url ? (
                                    <img src={att.url} alt={att.name} className="attachment-image" />
                                  ) : (
                                    <div className="attachment-file">
                                      <FileTextOutlined />
                                      <Text>{att.name}</Text>
                                    </div>
                                  )}
                                </div>
                              )
                            })}
                          </div>
                        )}
                        <div className="chat-message-text">{msg.content}</div>
                      </div>
                      <Text className="chat-message-time">
                        {new Date(msg.timestamp).toLocaleTimeString('zh-CN', {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </Text>
                    </div>
                  </div>
                ))}
                {loading && (
                  <div className="chat-message assistant">
                    <Avatar
                      className="chat-avatar"
                      icon={<RobotOutlined />}
                      style={{ backgroundColor: '#52C41A' }}
                    />
                    <div className="chat-message-content">
                      <div className="chat-message-bubble">
                        <Spin size="small" />
                        <Text style={{ marginLeft: 8 }}>正在思考...</Text>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </>
            )}
          </div>

          {/* 输入区域 */}
          <div className="chat-input-area">
            {fileList.length > 0 && (
              <div className="chat-file-list">
                {fileList.map((file) => {
                  // 处理文件点击打开（仅当文件已上传成功时）
                  const handleFileClick = () => {
                    if (file.status !== 'done') {
                      message.warning('文件尚未准备就绪')
                      return
                    }
                    
                    const attachment = (file as any).attachment as Attachment
                    const fileObj = (file as any).fileObj as File
                    const fileUrl = (file as any).fileUrl as string
                    
                    // 优先级：attachment.url > fileUrl > 从 fileObj 创建新的 Blob URL
                    let url = attachment?.url || fileUrl
                    
                    if (!url && fileObj) {
                      // 如果没有 URL，从文件对象创建 Blob URL
                      url = URL.createObjectURL(fileObj)
                      // 保存 URL 到 attachment 中，以便下次使用
                      if (attachment) {
                        attachment.url = url
                      }
                    }
                    
                    if (url) {
                      // 在新标签页中打开文件
                      window.open(url, '_blank')
                    } else {
                      message.warning('无法打开文件：文件数据不可用')
                    }
                  }
                  
                  return (
                    <div key={file.uid} className="chat-file-item">
                      <div className="chat-file-icon">
                        {file.type?.startsWith('image/') ? (
                          <PictureOutlined />
                        ) : file.type?.startsWith('audio/') ? (
                          <AudioOutlined />
                        ) : (
                          <FileTextOutlined />
                        )}
                      </div>
                      <div 
                        className="chat-file-info"
                        onClick={handleFileClick}
                        style={{ 
                          cursor: (file.status === 'done' || (file as any).attachment) ? 'pointer' : 'default',
                          flex: 1
                        }}
                        title={(file.status === 'done' || (file as any).attachment) ? '点击打开文件' : undefined}
                      >
                        <Text ellipsis style={{ maxWidth: 200, display: 'block' }}>
                          {file.name}
                        </Text>
                        {file.status === 'uploading' && (
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            上传中... {file.percent !== undefined && file.percent !== null ? file.percent : 0}%
                          </Text>
                        )}
                        {file.status === 'error' && (
                          <Text type="danger" style={{ fontSize: 12 }}>
                            上传失败
                          </Text>
                        )}
                        {file.status === 'done' && (
                          <Text type="success" style={{ fontSize: 12 }}>
                            上传完成
                          </Text>
                        )}
                      </div>
                      <Button
                        type="text"
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={(e) => {
                          e.stopPropagation()
                          handleRemove(file)
                        }}
                        disabled={file.status === 'uploading' && file.percent !== undefined && file.percent < 100}
                      />
                    </div>
                  )
                })}
              </div>
            )}
            {isRecording && (
              <div className="recording-indicator chat-recording-above-input">
                <div className="recording-dot"></div>
                <Text type="danger" style={{ fontSize: 12 }}>
                  正在录制: {formatRecordingTime(recordingTime)}
                </Text>
              </div>
            )}
            <div
              className="chat-input-wrapper"
              onMouseDown={(e) => {
                const t = e.target as HTMLElement
                if (t.closest('button') || t.closest('.ant-upload') || t.closest('input[type="file"]')) return
                if (t.tagName === 'TEXTAREA' || t.closest('textarea')) return
                chatTextareaRef.current?.focus()
              }}
            >
              <div className="chat-input-actions">
                <Upload
                  customRequest={handleUpload}
                  showUploadList={false}
                  accept="image/*,audio/*,.pdf,.doc,.docx,.txt,.xls,.xlsx"
                  multiple
                >
                  <Button type="text" icon={<PaperClipOutlined />} title="上传文件" />
                </Upload>
                {!isRecording ? (
                  <Button
                    type="text"
                    icon={<AudioOutlined />}
                    title="语音输入"
                    onClick={handleStartRecording}
                    className="voice-record-button"
                  />
                ) : (
                  <Button
                    type="text"
                    icon={<StopOutlined />}
                    title="停止录制"
                    onClick={handleStopRecording}
                    danger
                    className="voice-stop-button"
                  />
                )}
              </div>
              <div className="chat-textarea-shell">
                <TextArea
                  ref={chatTextareaRef}
                  className="chat-textarea"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="和AI助手说点什么..."
                  rows={8}
                  disabled={loading}
                />
              </div>
              <Button
                type="primary"
                icon={<SendOutlined />}
                onClick={handleSend}
                loading={loading}
                disabled={!inputValue.trim() && fileList.length === 0}
                className="chat-send-button"
              >
                发送
              </Button>
            </div>
            <div className="chat-footer">
              <Text type="secondary" style={{ fontSize: 12 }}>
                内容由AI生成，仅供参考
              </Text>
            </div>
          </div>
        </div>
      </div>

      {/* 重命名模态框 */}
      <Modal
        title="重命名对话"
        open={renameModalVisible}
        onOk={handleRenameConfirm}
        onCancel={() => {
          setRenameModalVisible(false)
          setRenameValue('')
        }}
        okText="确定"
        cancelText="取消"
      >
        <AntInput
          value={renameValue}
          onChange={(e) => setRenameValue(e.target.value)}
          placeholder="请输入对话名称"
          onPressEnter={handleRenameConfirm}
          autoFocus
        />
      </Modal>
    </div>
  )
}

export default Chat

